import clsx from 'clsx'

const LoadingSpinner = ({ colorClass = 'border-green-700' }) => (
  <div className={clsx(
    "inline-block animate-spin rounded-full h-5 w-5 border-b-2",
    colorClass
  )} />
)

const getGradientClass = (operatorColor) => {
  switch (operatorColor) {
    case 'tmobile':
      return 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'
    case 'orange':
      return 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200'
    case 'plus':
      return 'bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200'
    case 'play':
      return 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200'
    default:
      return 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'
  }
}

const getIconColorHex = (operatorColor) => {
  switch (operatorColor) {
    case 'tmobile':
      return '#E20074' // magenta
    case 'orange':
      return '#FF7900'
    case 'plus':
      return '#00A651'
    case 'play':
      return '#9333EA'
    default:
      return '#6B7280'
  }
}

const getBorderColor = (operatorColor) => {
  switch (operatorColor) {
    case 'tmobile':
      return 'border-green-200'
    case 'orange':
      return 'border-yellow-200'
    case 'plus':
      return 'border-blue-200'
    case 'play':
      return 'border-indigo-200'
    default:
      return 'border-gray-200'
  }
}

const KAMImplementationSection = ({ 
  operator, 
  stockExpositionData,
  salonAttributesData,
  visualizationData 
}) => {
  const stockData = stockExpositionData?.[operator.code]
  const attrData = salonAttributesData?.[operator.code]
  const visData = visualizationData?.operator_status_table?.[operator.code]
  
  const gradientClass = getGradientClass(operator.color)
  const iconColor = getIconColorHex(operator.color)
  const borderColor = getBorderColor(operator.color)

  // Get KAM exposition data
  const getExpositionValue = () => {
    const propEx = operator.operatorData.salons_with_proper_exposition
    const total = operator.operatorData.total_salons
    if (propEx !== null && propEx !== undefined) {
      return { value: propEx, total, percentage: Math.round((propEx / total) * 100) }
    }
    return null
  }

  // Get stock data
  const getStockData = () => {
    if (!stockData || stockData.loading) return { loading: true }
    if (stockData.error || !stockData.data?.supported) return null
    if (stockData.data.total_salons === 0) return null
    return {
      value: stockData.data.salons_with_stock,
      total: stockData.data.total_salons,
      percentage: stockData.data.percentage_stock_only
    }
  }

  // Get stock + exposition data
  const getStockExpositionData = () => {
    if (!stockData || stockData.loading) return { loading: true }
    if (stockData.error || !stockData.data?.supported) return null
    if (stockData.data.total_salons === 0) return null
    return {
      value: stockData.data.salons_with_stock_and_exposition,
      total: stockData.data.total_salons,
      percentage: stockData.data.percentage
    }
  }

  // Get Live Demo data
  const getLiveDemoData = () => {
    if (!attrData || attrData.loading) return { loading: true }
    if (attrData.error || !attrData.data?.live_demo) return null
    return {
      value: attrData.data.live_demo.salons_with_attribute,
      total: attrData.data.live_demo.total_salons,
      percentage: attrData.data.live_demo.percentage
    }
  }

  // Get Ambassador Device data
  const getAmbassadorData = () => {
    if (!attrData || attrData.loading) return { loading: true }
    if (attrData.error || !attrData.data?.ambassador_device) return null
    return {
      value: attrData.data.ambassador_device.salons_with_attribute,
      total: attrData.data.ambassador_device.total_salons,
      percentage: attrData.data.ambassador_device.percentage
    }
  }

  // Get operator exposition display
  const getOperatorExpositionDisplay = () => {
    if (operator.code === 'PLUS' || operator.code === 'P4') {
      if (!attrData || attrData.loading) return { loading: true }
      if (attrData.error || !attrData.data?.supported) {
        return { value: visData?.ekspozycja?.value || 'N/A' }
      }
      const count = attrData.data.visualization.salons_with_visualization
      const total = attrData.data.visualization.total_salons
      return {
        value: `${count}/${total}`,
        percentage: attrData.data.visualization.percentage,
        name: visData?.ekspozycja?.name
      }
    }
    if (!visData?.ekspozycja) return { value: 'N/A' }
    return {
      value: visData.ekspozycja?.value || 'N/A',
      name: typeof visData.ekspozycja === 'string' ? visData.ekspozycja : visData.ekspozycja?.name
    }
  }

  // Get operator stock + exposition display
  const getOperatorStockExpoDisplay = () => {
    if (operator.code === 'PLUS' || operator.code === 'P4') {
      if (!attrData || attrData.loading) return { loading: true }
      if (attrData.error || !attrData.data?.supports_dynamic) {
        return { value: visData?.stock_exposition_operator || 'N/A' }
      }
      const count = attrData.data.stock_and_visualization.salons_with_both
      const total = attrData.data.stock_and_visualization.total_salons
      return {
        value: `${count}/${total}`,
        percentage: attrData.data.stock_and_visualization.percentage
      }
    }
    return { value: visData?.stock_exposition_operator || 'N/A' }
  }

  // Implementation overrides from admin configuration
  const overrides = visualizationData?.implementation_overrides?.[operator.code] || {}

  const applyOverride = (metricKey, originalData) => {
    const override = overrides[metricKey]
    if (!override) return originalData

    if (override.override_type === 'not_applicable') {
      return null // ProgressRow renders N/A for null data
    }

    if (override.override_type === 'fixed_value') {
      if (['ekspozycja', 'towar', 'towar_ekspozycja', 'live_demo', 'ambassador_device'].includes(metricKey)) {
        const value = override.fixed_value_int ?? 0
        const total = override.fixed_total_int ?? 0
        return {
          value,
          total,
          percentage: total > 0 ? Math.round((value / total) * 100) : 0
        }
      }
      // Text metrics
      return { value: override.fixed_value_str || 'N/A' }
    }

    return originalData
  }

  const expositionData = applyOverride('ekspozycja', getExpositionValue())
  const stockDataResult = applyOverride('towar', getStockData())
  const stockExpoResult = applyOverride('towar_ekspozycja', getStockExpositionData())
  const liveDemoData = applyOverride('live_demo', getLiveDemoData())
  const ambassadorData = applyOverride('ambassador_device', getAmbassadorData())
  const operatorExpoDisplay = getOperatorExpositionDisplay()
  const operatorStockExpoDisplay = getOperatorStockExpoDisplay()

  const ProgressRow = ({ label, data, colorPositive = 'bg-green-500', colorMid = 'bg-yellow-500', colorLow = 'bg-red-500' }) => (
    <div className="bg-white/80 p-2.5 rounded-lg">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-semibold text-gray-700">{label}</span>
        <span className="text-xs font-bold text-gray-800">
          {data?.loading ? (
            <LoadingSpinner colorClass="border-gray-500" />
          ) : data ? (
            <>
              {data.value}/{data.total}{' '}
              <span className="text-gray-500">({data.percentage}%)</span>
            </>
          ) : (
            <span className="text-gray-500">N/A</span>
          )}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-1.5">
        <div 
          className={clsx(
            "h-1.5 rounded-full",
            data && !data.loading 
              ? (data.percentage >= 50 ? colorPositive : data.percentage >= 25 ? colorMid : colorLow)
              : 'bg-gray-300'
          )}
          style={{ width: `${data && !data.loading ? data.percentage : 0}%` }}
        />
      </div>
    </div>
  )

  return (
    <div className={clsx("p-4 rounded-xl border", gradientClass)}>
      {/* Implementacja KAM Header */}
      <h4 className="text-xs font-bold text-gray-700 mb-3 flex items-center">
        <svg className="w-4 h-4 mr-2" style={{ color: iconColor }} fill="currentColor" viewBox="0 0 20 20">
          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
        </svg>
        Implementacja KAM
      </h4>
      
      <div className="space-y-2.5">
        {/* Ekspozycja (KAM) */}
        {expositionData ? (
          <ProgressRow 
            label="Ekspozycja" 
            data={expositionData}
          />
        ) : (
          <div className="bg-white/80 p-2.5 rounded-lg">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-semibold text-gray-700">Ekspozycja</span>
              <span className="text-xs font-bold text-red-600">Brak danych</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div className="bg-gray-300 h-1.5 rounded-full" style={{ width: '0%' }} />
            </div>
          </div>
        )}

        {/* Towar (KAM) */}
        <ProgressRow 
          label="Towar" 
          data={stockDataResult}
        />

        {/* Towar + Ekspozycja (KAM) */}
        <ProgressRow 
          label="Towar + Ekspozycja" 
          data={stockExpoResult}
        />

        {/* Live Demo */}
        <ProgressRow 
          label="📱 Live Demo" 
          data={liveDemoData}
          colorPositive="bg-cyan-500"
          colorMid="bg-cyan-400"
          colorLow="bg-cyan-300"
        />

        {/* Urządzenie ambasadorskie */}
        <ProgressRow 
          label="🏆 Urządzenie ambasadorskie" 
          data={ambassadorData}
          colorPositive="bg-amber-500"
          colorMid="bg-amber-400"
          colorLow="bg-amber-300"
        />
      </div>

      {/* Dane operatora - inside the same container */}
      <div className={clsx("mt-3 pt-3 border-t", borderColor)}>
        <h4 className="text-xs font-bold text-gray-700 mb-2.5 flex items-center">
          <svg className="w-4 h-4 mr-2" style={{ color: iconColor }} fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
          </svg>
          Dane operatora
        </h4>
        
        {/* Ekspozycja (operator) */}
        <div className="bg-white/80 p-2.5 rounded-lg mb-2">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-gray-600">Ekspozycja</span>
            <div className="text-right">
              {operatorExpoDisplay.name && (
                <p className="text-xs font-bold text-gray-800">{operatorExpoDisplay.name}</p>
              )}
              <p className="text-xs font-semibold" style={{ color: '#16a34a' }}>
                {operatorExpoDisplay.loading ? (
                  <LoadingSpinner colorClass="border-green-600" />
                ) : (
                  <>
                    {operatorExpoDisplay.value}
                    {operatorExpoDisplay.percentage !== undefined && (
                      <span className="text-gray-500 ml-1">({operatorExpoDisplay.percentage}%)</span>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Towar + Ekspozycja (operator) */}
        <div className="bg-white/80 p-2.5 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-gray-600">Towar + Ekspozycja</span>
            <span className="text-xs font-bold text-gray-800">
              {operatorStockExpoDisplay.loading ? (
                <LoadingSpinner colorClass="border-gray-600" />
              ) : (
                <>
                  {operatorStockExpoDisplay.value}
                  {operatorStockExpoDisplay.percentage !== undefined && (
                    <span className="text-gray-500 ml-1">({operatorStockExpoDisplay.percentage}%)</span>
                  )}
                </>
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default KAMImplementationSection
